import { useMutation, useQueryClient } from '@tanstack/react-query';
import { responseDocumentClient } from '../../../../lib/connect/clients';
import { reviewKeys, useReviewList } from '../reviews/useProposalReviews';
import { flattenSections, useOutlineSections } from './workingOutlineHooks';
/** WRT-17 §3.6: Ready only while the region still has the hash that was marked. */ export function sectionStatus(s) {
    if (s.orphanedAt) return 'missing';
    if (s.contentWords <= 0) return 'empty';
    if (s.readyRegionSha256 !== '' && s.readyRegionSha256 === s.contentRegionSha256) return 'ready';
    return 'draft';
}
/** The volume's sections that live in `documentId`, in document order. */ export function useVolumeSections(projectId, volumeId, documentId) {
    const outline = useOutlineSections(projectId, volumeId);
    const all = flattenSections(outline.data ?? []).filter((s)=>!!documentId && s.sourceDocumentId === documentId).sort((a, b)=>a.sortOrder - b.sortOrder);
    let n = 0;
    const live = [];
    const removed = [];
    for (const node of all){
        const status = sectionStatus(node);
        if (status === 'missing') removed.push({
            node,
            n: 0,
            status
        });
        else live.push({
            node,
            n: ++n,
            status
        });
    }
    return {
        live,
        removed,
        isLoading: outline.isLoading,
        isError: outline.isError
    };
}
/** The open review with the earliest start date, for "Mark ready for {name}". */ export function useNextReviewName(projectId) {
    const list = useReviewList(projectId);
    return (list.data ?? []).filter((r)=>r.status === 'open').sort((a, b)=>a.startsOn.localeCompare(b.startsOn))[0]?.name;
}
export function useMarkSectionReady(projectId, volumeId) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (v)=>responseDocumentClient.markSectionReady({
                projectId,
                volumeId,
                ...v
            }),
        onSuccess: ()=>Promise.all([
                qc.invalidateQueries({
                    queryKey: [
                        'outline-sections',
                        projectId
                    ]
                }),
                qc.invalidateQueries({
                    queryKey: [
                        'officeVersions'
                    ]
                }),
                qc.invalidateQueries({
                    queryKey: [
                        'officeWorkspace'
                    ]
                }),
                qc.invalidateQueries({
                    queryKey: reviewKeys.states(projectId)
                })
            ])
    });
}
export function usePutHeadingBack(projectId, volumeId) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (sectionId)=>responseDocumentClient.putSectionHeadingBack({
                projectId,
                volumeId,
                sectionId
            }),
        onSuccess: ()=>Promise.all([
                qc.invalidateQueries({
                    queryKey: [
                        'outline-sections',
                        projectId
                    ]
                }),
                qc.invalidateQueries({
                    queryKey: [
                        'officeWorkspace'
                    ]
                })
            ])
    });
}
/** Footer text for the outline: "Page limit 25 · using 17.4". */ export function pageFooter(budget) {
    if (!budget) return undefined;
    const pages = Number.isInteger(budget.pages) ? String(budget.pages) : budget.pages.toFixed(1);
    const limit = Number.isInteger(budget.pageLimit) ? String(budget.pageLimit) : budget.pageLimit.toFixed(1);
    return budget.pageLimit > 0 ? `Page limit ${limit} · using ${pages}` : `Using ${pages} pages`;
}
